import React from "react";
import { Box, Button, Typography, CssBaseline, Card } from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import AppTheme from "../../theme/AppTheme";
import { InputField } from "../../components/Form/InputField";
import { useNavigate } from "react-router-dom";
import { notifyError, notifySuccess } from "../../components/ToastContainer/Toast/Toast";
import { Alert } from "@mui/material";
import { changePassword } from "../../service/Auth/authService";

interface ChangePasswordFormValues {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

const initialValues: ChangePasswordFormValues = {
  old_password: "",
  new_password: "",
  confirm_password: "",
};

const passwordValidationSchema = Yup.object().shape({
  old_password: Yup.string().required("Old Password is required"),
  new_password: Yup.string()
    .required("New password is required")
    .min(6, "Password should be at least 6 characters")
    .max(25, "Password should not be more than 25 characters")
    .matches(/^\S*$/, "Password should not contain spaces")
    .test(
      "uppercase",
      "Password must contain at least ONE uppercase letter (A-Z)",
      value => /[A-Z]/.test(value || "")
    )
    .test(
      "number",
      "Password must contain at least ONE number (0-9)",
      value => /\d/.test(value || "")
    )
    .test(
      "specialChar",
      "Password must contain at least ONE special character from @$!%*?&#",
      value => /[@$!%*?&#]/.test(value || "")
    ),
  confirm_password: Yup.string()
    .required("Confirm your password")
    .oneOf([Yup.ref("new_password")], "Passwords not match"),
});

const ChangePassword: React.FC = (props: { disableCustomTheme?: boolean }) => {
  const navigate = useNavigate();

  const handleSubmit = async (values: ChangePasswordFormValues,actions: FormikHelpers<ChangePasswordFormValues>) => {
    const postPayload = {
      ...values
    }
    try {
      const response = await changePassword(postPayload);
      if([200,201].includes(response.status)){
        notifySuccess(response?.data?.message || "Password updated successfully!");
        actions.resetForm();
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || "Something Went Wrong");
    }  
  };

  return (
    <AppTheme {...props} >
      <CssBaseline enableColorScheme />
      <Box>
        {/* <Typography variant="h5" fontWeight="600" mb={3}>
                    Change Password
                </Typography> */}
        <Formik
          initialValues={initialValues}
          validationSchema={passwordValidationSchema}
          onSubmit={handleSubmit}
        >
          {({setFieldValue}) => (
            <Form>
              <Box display="grid" gap={2}>
                <Card variant="outlined">
                  <Alert
                    // severity="warning"
                    variant="outlined"
                    sx={{
                      p: 1,
                      fontSize: "0.85rem",
                      backgroundColor: "#fff3e0",
                      borderColor: "#ffcc80",
                      color: "#5d4037",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Password should be 6–25 characters, with at least one uppercase, one lowercase, one number, and one special character.
                    </Typography>
                  </Alert>

                  <Box display="grid" gap={2} sx={{ mt: 2 }}>
                    <InputField
                      name="old_password"
                      label="Old Password"
                      type="password"
                      isRequired
                      placeholder="Enter Old Password"
                      onBlur={(e) =>
                        setFieldValue("old_password", e.target.value.trim())
                      }
                    />
                    <InputField
                      name="new_password"
                      label="New Password"
                      type="password"
                      isRequired
                      placeholder="Enter New Password"
                      onBlur={(e) =>
                        setFieldValue("new_password", e.target.value.trim())
                      }
                    />
                    <InputField
                      name="confirm_password"
                      label="Confirm Password"
                      type="password"
                      isRequired
                      placeholder="Enter Confirm Password"
                      onBlur={(e) =>
                        setFieldValue("confirm_password", e.target.value.trim())
                      }
                    />
                  </Box>

                  <Box mt={4} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                      onClick={() => navigate(-1)}
                      variant="outlined"
                      color="secondary"
                    >
                      Back
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                      Update
                    </Button>
                  </Box>
                </Card>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </AppTheme>
  );
};

export default ChangePassword;
